import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { Input } from '@ast/magma/components/input';

import { CharacterRequirement, FieldBehaviorMode } from '@app/queryTyping';

import { Fields } from '@app/common/configurable-wizards/types';
import { getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { FormValidationOptions } from '@app/common/types/form';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { CFFormField } from '../common/CFFormField';

import { UsernameValidationRules } from './types';

export interface UsernameInputProps {
  field: Fields['Username'];
  fieldName: string;
  onFocusChange?: (isActive: boolean) => void
}

export type UsernameInputHtmlProps = Pick<React.InputHTMLAttributes<HTMLInputElement>, 'aria-describedby'>;

export const UsernameInput: React.FC<UsernameInputProps & UsernameInputHtmlProps> = ({
  field,
  fieldName,
  onFocusChange,
  ...htmlInputProps
}) => {
  const { t } = useTranslation();
  const { register, formState: { errors } } = useFormContext();
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(fieldName, errors, commonValidationMessages);
  const { userNamePolicy } = field;

  const onFocusChangeInternal = (isActive: boolean) => {
    if (typeof onFocusChange === 'function') {
      onFocusChange(isActive);
    }
  };

  const validationOptions = useMemo<FormValidationOptions>(() => {
    const rules:FormValidationOptions = {
      [UsernameValidationRules.required]: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
    };

    if (!userNamePolicy) { return rules; }

    if (userNamePolicy.minimumCharacterLength) {
      rules[UsernameValidationRules.minLength] = {
        value: userNamePolicy.minimumCharacterLength,
        message: t(
          'username.validation.minlength|Minimum length - validation message',
          'Username must be at least {{minLength}} characters long',
          { minLength: userNamePolicy.minimumCharacterLength },
        ),
      };
    }

    if (userNamePolicy.maximumCharacterLength) {
      rules[UsernameValidationRules.maxLength] = {
        value: userNamePolicy.maximumCharacterLength,
        message: t(
          'username.validation.maxlength|Maximum length - validation message',
          'Username must be up to {{maxLength}} characters long',
          { maxLength: userNamePolicy.maximumCharacterLength },
        ),
      };
    }

    if (userNamePolicy.firstCharacterMustBeAlphabetic) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.firstCharAlphabetic]: (value) => (
          /^[A-z]/.test(value) || t(
            'username.validation.first-alphabetic|First letter must be alphabetic - validation message',
            'First letter must be alphabetic',
          ) as string
        ),
      };
    }

    if (userNamePolicy.anyCharacterMustBeAlphabetic) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.anyAlphabetic]: (value) => (
          /[A-z]/g.test(value) || t(
            'username.validation.any-alphabetic|Must contain at least one alphabetic letter - validation message',
            'Must contain at least one alphabetic letter',
          ) as string
        ),
      };
    }

    if (userNamePolicy.lowerCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.anyLowerCase]: (value) => (
          /[a-z]/g.test(value) || t(
            'username.validation.any-lowercase|At least one lowercase letter - validation message',
            'Must contain at least one lowercase letter',
          ) as string
        ),
      };
    } else if (userNamePolicy.lowerCaseRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.noLowerCase]: (value) => (
          !/[a-z]/g.test(value) || t(
            'username.validation.no-lowercase|Must not contain lowercase letters - validation message',
            'Must not contain lowercase letters',
          ) as string
        ),
      };
    }

    if (userNamePolicy.upperCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.anyUpperCase]: (value) => (
          /[A-Z]/g.test(value) || t(
            'username.validation.any-uppercase|At least one uppercase letter - validation message',
            'Must contain at least one uppercase letter',
          ) as string
        ),
      };
    } else if (userNamePolicy.upperCaseRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.noUpperCase]: (value) => (
          !/[A-Z]/g.test(value) || t(
            'username.validation.no-uppercase|Must not contain uppercase letters - validation message',
            'Must not contain uppercase letters',
          ) as string
        ),
      };
    }

    if (userNamePolicy.numberRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.anyNumber]: (value) => (
          /[\d]/g.test(value) || t(
            'username.validation.any-number|At least one number - validation message',
            'Must contain at least one number',
          ) as string
        ),
      };
    } else if (userNamePolicy.numberRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.noNumber]: (value) => (
          !/[\d]/g.test(value) || t(
            'username.validation.no-number|Must not contain numbers - validation message',
            'Must not contain numbers',
          ) as string
        ),
      };
    }

    if (userNamePolicy.specialCharacterRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.anySpecialChar]: (value) => (
          /[^A-Za-z0-9]/g.test(value) || t(
            'username.validation.any-special-char|At least one special char - validation message',
            'Must contain at least one special character',
          ) as string
        ),
      };
    } else if (userNamePolicy.specialCharacterRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [UsernameValidationRules.noSpecialChar]: (value) => (
          !/[^A-Za-z0-9]/g.test(value) || t(
            'username.validation.no-special-char|Must not contain special char - validation message',
            'Must not contain special characters',
          ) as string
        ),
      };
    }

    return rules;
  }, [field]);

  const { onBlur, ...restRegiesterOptions } = register(fieldName, validationOptions);

  return (
    <CFFormField error={error}>
      <Input
        {...htmlInputProps}
        type="text"
        autoComplete="username"
        label={field.label || ''}
        aria-invalid={!!error}
        minLength={userNamePolicy?.minimumCharacterLength || field.minLength}
        maxLength={userNamePolicy?.maximumCharacterLength || field.maxLength}
        data-stable-name={`${field.id}Input`}
        onFocus={() => onFocusChangeInternal(true)}
        onBlur={(event) => {
          onBlur(event);
          onFocusChangeInternal(false);
        }}
        {...restRegiesterOptions}
      />
    </CFFormField>
  );
};
